import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { ApolloConsumer } from '@apollo/client';
import ArticleDetail, {
  ArticleBreadcrumbsContainer,
  ArticleGrid,
  ArticleMainContainer,
  ArticleSidebar,
} from '@fuww/library/src/Article';
import { GridCell } from '@fuww/library/src/Grid';
import {
  DesktopAdUnitContainer,
  TabletAdUnitContainer,
  MobileAdUnitContainer,
} from '@fuww/library/src/Advertisement/AdUnitContainer';
import dynamic from 'next/dynamic';
import ExecutiveTheme from '@fuww/library/src/Theme/Executive';
import HiddenLoader from '@fuww/library/src/HiddenLoader';
import Head from '../../Head';
import withQuery from '../../../lib/withQuery';
import throw404 from '../../../lib/throw404';
import getArticleJsonLdData from '../../../lib/getArticleJsonLdData';
import articleQuery
  from '../../../lib/queries/news/articleQuery.graphql';
import articleTranslationsQuery
  from '../../../lib/queries/news/articleTranslations.graphql';
import getArticleSubjectType from '../../../lib/getArticleSubjectType';
import {
  articleImageConfigurations,
  articleLightboxImageConfigurations,
  authorImageConfigurations,
} from '../../../lib/imageConfigurations';
import increaseHitsMutation from
  '../../../lib/queries/news/increaseHitsMutation.graphql';
import { metaImageDimensions } from '../../../lib/constants';
import BreadCrumbs from './BreadCrumb';
import Main from './Main';
import RelatedNews from './RelatedNews';
import RelatedJobs from './RelatedJobs';
import RelatedProfiles from './RelatedProfiles';
import getDataLayerVariables from './getDataLayerVariables';
import isLoading from '../../../lib/loading';
import { useSiteContext } from '../../SiteContext';
import InView from '../../InView';
import AdUnit from '../../../lib/adunit';
import getSourceSet from '../../../lib/getSourceSet';
import isPositiveSentiment from '../../../lib/isPositiveSentiment';
import splitImageUrls from './splitImageUrls';
import getImageUrlsWithDefaults from './getImageUrlsWithDefaults';
import { AccessLevel } from '../../../lib/graphql/api/graphql';
import Layout from '../../Layout';
import {
  getTranslationsByLanguages,
  getOriginalArticle,
} from '../../../lib/selectTranslatedArticleLinks';
import HrefLangTag from './HrefLangTag';

const DynamicSideBar = dynamic(() => import('./SideBar'), {
  ssr: false,
  loading: isLoading,
});

export const ArticleWithLayout = ({
  data,
  ArticleComponent,
  apolloClient,
  showLoader,
  error,
  orderStatus,
  translationData,
}) => {
  const {
    prefix, hasLocalNews, url, origin, newsboard, locale,
  } = useSiteContext();
  const article = data?.universalNewsArticle;

  const translationsByLanguage = translationData
    ? getTranslationsByLanguages(translationData, locale)
    : {};
  const originalArticle = translationData
    ? getOriginalArticle(translationData)
    : null;
  const translationLanguages = Object.keys(translationsByLanguage);

  useEffect(() => {
    if (article) {
      apolloClient.mutate({
        mutation: increaseHitsMutation,
        variables: {
          newsboard,
          id: Number(article.id),
        },
      });
    }
  }, [article, apolloClient, hasLocalNews, prefix, newsboard]);

  if (showLoader) {
    return (
      <ArticleGrid>
        <GridCell
          sm={12}
          lg={8}
          tag="article"
          padding="0px 8px 0"
        >
          <ArticleDetail showLoader />
        </GridCell>
      </ArticleGrid>
    );
  }

  if (!article) {
    if (error) {
      return null;
    }

    return throw404();
  }

  const {
    id, title, creator, keywords, tags,
    insertedAt, description, imageUrls: originalImageUrls,
    partnerContent, scores, featured, pressRelease, category, accessLevel,
  } = article;
  const imageUrlsWithDefaults = getImageUrlsWithDefaults(
    originalImageUrls,
    articleImageConfigurations,
  );

  const {
    metaImageUrl,
    imageUrls,
    lightboxImageUrls,
  } = splitImageUrls(imageUrlsWithDefaults);

  const {
    name: authorName,
  } = { ...creator };

  const robotsMeta = partnerContent
    ? 'index, nofollow' : null;

  const dataLayerVariables = getDataLayerVariables(article);

  const sourceSet = getSourceSet(
    articleImageConfigurations.slice(1),
    imageUrls,
  );

  const articleContent = (
    <ArticleGrid>
      <ArticleBreadcrumbsContainer>
        <BreadCrumbs article={article} />
      </ArticleBreadcrumbsContainer>
      <ArticleMainContainer>
        <Main
          article={article}
          ArticleComponent={ArticleComponent}
          metaImageUrl={metaImageUrl}
          imageUrls={imageUrls}
          sourceSet={sourceSet}
          lightboxSourceSet={lightboxImageUrls.map(
            (imageUrl, index) => (`${imageUrl} ${
              articleLightboxImageConfigurations[index].width}w`),
          ).join(', ')}
          showLoader={showLoader}
          orderStatus={orderStatus}
          translationsByLanguage={translationsByLanguage}
          originalArticle={originalArticle}
        />
        {
          isPositiveSentiment(scores) && (
            <InView>
              <RelatedJobs tagSlugs={tags.map(({ slug }) => slug)} />
            </InView>
          )
        }
        <MobileAdUnitContainer>
          <AdUnit
            adUnit="mobile_article_bottom"
            sizes={[[1, 1], [320, 240], [300, 250]]}
            minHeight="240px"
          />
        </MobileAdUnitContainer>
        <TabletAdUnitContainer>
          <AdUnit
            adUnit="tablet_article_bottom"
            sizes={[[1, 1], [300, 250]]}
            minHeight="250px"
          />
        </TabletAdUnitContainer>
        <DesktopAdUnitContainer>
          <AdUnit
            adUnit="article_bottom"
            sizes={[[1, 1], [300, 250]]}
            minHeight="250px"
          />
        </DesktopAdUnitContainer>
        <InView>
          <RelatedNews
            tagSlugs={tags.map(({ slug }) => slug)}
            articleId={id}
            showCategory
            showImage
          />
        </InView>
        <InView>
          <RelatedProfiles newsTags={tags.map(({ slug }) => slug)} />
        </InView>
      </ArticleMainContainer>
      <ArticleSidebar>
        <InView>
          <DynamicSideBar keywords={keywords} articleId={id} />
        </InView>
      </ArticleSidebar>
    </ArticleGrid>
  );
  return (
    <Layout isExecutive={accessLevel !== AccessLevel.FREE}>
      <Head
        title={title}
        description={description}
        imageUrl={metaImageUrl}
        imageDimensions={metaImageDimensions}
        insertedAt={insertedAt}
        author={authorName}
        robotsMeta={robotsMeta}
        jsonLdData={getArticleJsonLdData(article, url, origin)}
        subjectType={getArticleSubjectType(prefix, hasLocalNews)}
        subjectId={id}
        payload={{ newsboard }}
        addCanonical
        prelude={(
          <link
            rel="modulepreload"
            as="script"
            crossOrigin="anonymous"
            href="https://storage.googleapis.com/media-fashionunited-com/media/details-menu/index.min.js"
          />
        )}
        id={id}
        featured={featured}
        pressRelease={pressRelease}
        partnerContent={partnerContent}
        category={category}
        accessLevel={accessLevel}
      >
        <script
          async
          type="module"
          crossOrigin="anonymous"
          src="https://storage.googleapis.com/media-fashionunited-com/media/details-menu/index.min.js"
        />
        <script
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: `window.dataLayer = window.dataLayer || []; \
  window.dataLayer.push(${JSON.stringify(dataLayerVariables)});`,
          }}
        />
        {originalArticle?.newsArticle && (
          <HrefLangTag
            key={originalArticle.locale}
            locale={originalArticle.locale}
            article={originalArticle.newsArticle}
            origin={origin}
          />
        )}
        {translationLanguages.length > 0 && translationLanguages.map(
          (translationKey) => {
            const {
              locale: translationLocale,
              newsArticle,
            } = translationsByLanguage[translationKey];

            return (
              <HrefLangTag
                key={translationLocale}
                locale={translationLocale}
                article={newsArticle}
                origin={origin}
              />
            );
          },
        )}
      </Head>
      { accessLevel === AccessLevel.FREE ? articleContent : (
        <ExecutiveTheme>{articleContent}</ExecutiveTheme>
      )}
    </Layout>
  );
};

export const articlePropertyTypes = {
  variables: PropTypes.shape({}).isRequired,
};

ArticleWithLayout.propTypes = {
  ...articlePropertyTypes,
  data: PropTypes.shape({}),
  ArticleComponent: PropTypes.elementType,
  apolloClient: PropTypes.shape({
    mutate: PropTypes.func,
  }).isRequired,
  translationData: PropTypes.shape({}),
  showLoader: PropTypes.bool,
  error: PropTypes.shape({}),
  orderStatus: PropTypes.string,
};
ArticleWithLayout.defaultProps = {
  data: {},
  ArticleComponent: undefined,
  translationData: null,
  showLoader: false,
  error: null,
  orderStatus: null,
};

const ArticleWithLayoutWithApolloClient = React.forwardRef(
  (properties, reference) => (
    <ApolloConsumer>
      {(client) => (
        <ArticleWithLayout
          {...properties}
          apolloClient={client}
          ref={reference}
        />
      )}
    </ApolloConsumer>
  ),
);

const ArticleWithQuery = withQuery(ArticleWithLayoutWithApolloClient, {
  renderAlways: true,
});

export const Article = ({
  id,
  ArticleComponent,
  orderStatus,
  translationData,
}) => {
  const { newsboard } = useSiteContext();

  return (
    <ArticleWithQuery
      query={articleQuery}
      variables={{
        newsboard,
        id,
        imageConfigurations: [
          ...articleImageConfigurations,
          ...articleLightboxImageConfigurations,
        ],
        authorImageConfigurations,
      }}
      errorMessage="Error loading this news article..."
      ArticleComponent={ArticleComponent}
      loader={<HiddenLoader />}
      orderStatus={orderStatus}
      translationData={translationData}
    />
  );
};

Article.propTypes = {
  id: PropTypes.number,
  ArticleComponent: PropTypes.elementType,
  orderStatus: PropTypes.string,
  translationData: PropTypes.shape({}),
};

Article.defaultProps = {
  id: null,
  ArticleComponent: undefined,
  orderStatus: null,
  translationData: undefined,
};

const ArticleWithHrefLang = ({
  data: translationData,
  showLoader: translationsShowLoader,
  error: translationsError,
  ...otherProperties
}) => (
  <Article
    translationData={translationData}
    {...otherProperties}
  />
);

ArticleWithHrefLang.propTypes = {
  data: PropTypes.shape({}),
  showLoader: PropTypes.bool,
  error: PropTypes.shape({}),
};

ArticleWithHrefLang.defaultProps = {
  data: null,
  showLoader: false,
  error: null,
};

const ArticleWithHrefLangWithQuery = withQuery(ArticleWithHrefLang, {
  renderAlways: true,
});

const ArticleWithHrefLangTags = ({
  id,
  orderStatus,
}) => {
  const { newsboard, hasLocalNews } = useSiteContext();

  return (
    <ArticleWithHrefLangWithQuery
      query={articleTranslationsQuery}
      variables={{
        duplicatesOf: {
          id,
          newsboard,
          local: hasLocalNews,
        },
      }}
      fetchPolicy="cache-first"
      errorMessage=""
      loader={<HiddenLoader />}
      id={id}
      orderStatus={orderStatus}
    />
  );
};

ArticleWithHrefLangTags.propTypes = {
  id: PropTypes.number,
  orderStatus: PropTypes.string,
};

ArticleWithHrefLangTags.defaultProps = {
  id: null,
  orderStatus: null,
};

export default ArticleWithHrefLangTags;
